import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchMaintenanceList, type MaintenanceListParams } from "../services/maintenanceService";
import type { MaintenanceRecord } from "../types";

/** Registros por página que se piden al API (su tope). */
export const PAGE_SIZE = 500;

// Lista de mantenimientos de la compañía con estado de carga/error y recarga.
// El API pagina por `page`; si hay más de PAGE_SIZE, `total` lo dice
// (`meta.totalItems`) y `loadMore` trae la página siguiente.
export function useMaintenanceRecords(params: MaintenanceListParams = {}) {
  const { t } = useTranslation("common");
  const [rows, setRows] = useState<MaintenanceRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = JSON.stringify(params);
  // `t` por ref: si su identidad cambia, no debe volver a pedir la lista (y
  // borrar lo que trajo «Cargar más»).
  const tRef = useRef(t);
  tRef.current = t;

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items, meta } = await fetchMaintenanceList({ ...JSON.parse(key), limit: PAGE_SIZE, page: 1 });
      setRows(items);
      setPage(1);
      setTotal(Math.max(items.length, Number(meta?.totalItems) || 0));
    } catch {
      setError(tRef.current("mantenimiento.loadError"));
    } finally {
      setLoading(false);
    }
  }, [key]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const next = page + 1;
      const { items, meta } = await fetchMaintenanceList({ ...JSON.parse(key), limit: PAGE_SIZE, page: next });
      setRows((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...items.filter((r) => !seen.has(r.id))];
      });
      setPage(next);
      if (meta?.totalItems) setTotal(Number(meta.totalItems));
    } catch {
      setError(tRef.current("mantenimiento.loadError"));
    } finally {
      setLoadingMore(false);
    }
  }, [key, page]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { rows, setRows, total, truncated: total > rows.length, loading, loadingMore, error, reload, loadMore };
}
